/**
 * SOLID Review
 * S — Strong. One concern: build the Framework's view models with what each needs — the Application's ports, the browser's roles,
 *     the shared presentation state.
 * O — Good. A new view model is one method here.
 * L — N/A — no hierarchy.
 * I — Good. One method per view model, and the shared collaborators the bindings reach.
 * D — Good. The ports and roles it is handed; the view models it builds are this layer's own classes.
 */
import type { IPortClientUseCases } from '../../application/ports/ClientUseCases.contract';
import type { IPortClientStateView } from '../../application/ports/ClientStateView';
import type { IConfigDepViewPlatform } from './platform/IConfigDepViewPlatform';
import type { ClientRequests } from '../client/ClientRequests';
import type { UiState } from '../stores/UiState';
import type { IServiceLiveKitAdapter } from '../../application/ports/IGatewayLiveKit';
import type { IServiceWsConnection } from '../uml-components/ComponentFramework';
import { DiceAudio } from './DiceAudio';
import { AuthRouterViewModel, type IConfigDepAuthRouter } from './AuthRouterViewModel';
import { LiveKitSessionController, type IConfigDepLiveKitSession } from './LiveKitSessionController';
import { AudioRouteMonitor } from './AudioRouteMonitor';
import { DisconnectionMonitor, type IPortDisconnectionOverlay } from './DisconnectionMonitor';
import { PageUnloadGuard, type IPortPageUnloadGuard } from './PageUnloadGuard';
import { IdentityModalController } from './IdentityModalController';
import { LegalDocumentLoader } from './LegalDocumentLoader';
import { PaymentBalancePoller } from './PaymentBalancePoller';
import { ReferralValidation } from './ReferralValidation';
import { AppRootViewModel, type IConfigDepAppRoot } from './AppRootViewModel';
import { ConsentModalController } from './ConsentModalController';
import { CookiePreferencesViewModel } from './CookiePreferencesViewModel';
import { LoginViewModel, type IConfigDepLogin } from './LoginViewModel';
import { EnterCodeViewModel } from './EnterCodeViewModel';
import { VerifyCodeViewModel } from './VerifyCodeViewModel';
import { ProfileViewModel } from './ProfileViewModel';
import { WelcomeViewModel, type IConfigDepWelcome, type IServiceWelcomeStage } from './WelcomeViewModel';
import { DiceRollingSound } from './DiceRollingSound';
import { LegalModalViewModel, type LegalTab } from './LegalModalViewModel';
import { ConsentReacceptanceViewModel } from './ConsentReacceptanceViewModel';
import { GdprDeletionViewModel } from './GdprDeletionViewModel';
import { GdprExportViewModel } from './GdprExportViewModel';
import { GameSceneViewModel, type IConfigDepGameScene, type IConfigGlDebug } from './GameSceneViewModel';
import { SceneRuntime } from './SceneRuntime';
import { SceneCanvasViewModel, type IConfigDepSceneCanvas } from './SceneCanvasViewModel';
import { CupMotion } from './CupMotion';
import { FrameWarmup } from './FrameWarmup';
import { PageStyleSheet } from './PageStyleSheet';
import { ControlPanelViewModel } from './ControlPanelViewModel';
import { MediaHealthPoller } from './MediaHealthPoller';
import { LobbyViewModel, type IConfigDepLobby } from './LobbyViewModel';
import { IdentityModalViewModel } from './IdentityModalViewModel';
import { ReconnectingOverlayViewModel } from './ReconnectingOverlayViewModel';
import { QrModalViewModel } from './QrModalViewModel';
import { AdContainerViewModel } from './AdContainerViewModel';
import { AdPushModel } from './AdPushModel';
import { HealthIndicatorViewModel } from './HealthIndicatorViewModel';
import { MaintenanceViewModel } from './MaintenanceViewModel';
import { MobileLayoutViewModel } from './MobileLayoutViewModel';
import { TopBannerAdViewModel } from './TopBannerAdViewModel';
import { TileWidthModel } from './TileWidthModel';
import { TileVolumeModel } from './TileVolumeModel';
import { VideoTrackModel } from './VideoTrackModel';
import { BuyCreditsViewModel } from './BuyCreditsViewModel';
import { UsageReportViewModel } from './UsageReportViewModel';
import { PaymentSuccessViewModel } from './PaymentSuccessViewModel';
import { FunnelStepsViewModel } from './FunnelStepsViewModel';

/** What the factory builds view models from: the Framework's two ports, the browser, the request contexts, the shared state. */
export interface IConfigDepViewModels {
  readonly useCases: IPortClientUseCases;
  readonly view: IPortClientStateView;
  readonly platform: IConfigDepViewPlatform;
  readonly requests: ClientRequests;
  readonly ui: UiState;
  /** The host's LAZY LiveKit client factory (a host resource, rule 2.3) — null where the host has no video. */
  readonly liveKit: IServiceLiveKitAdapter | null;
  /** The websocket's live state (a host resource, rule 2.3) — the game screen's watchdog reads it (P3). */
  readonly connection: IServiceWsConnection;
}

/**
 * THE FRAMEWORK'S VIEW-MODEL FACTORY (frontend-framework-oo P1, 2026-10-29, §28.293) — built ONCE by the composition root
 * (`frontendRoot.ts`) over the assembled Framework component's ports, and handed to the tree by `ViewModelProvider`; a view binds
 * to what it builds with `useViewModel((views) => views.<model>())`. The objects that were module state are its fields: the request
 * contexts' counter (`ClientRequests`), the shared presentation state (`UiState`), the dice sound's context (`DiceAudio`); a view
 * model a page holds once — the auth router's session start — it builds once. Since P2 (§28.294) it builds the hooks' controllers,
 * one per mount: the LiveKit session, the audio route, the disconnection overlays, the unload guard, the name modal, a legal
 * document, the balance poll, the referral field, a client job. Since P3 (§28.295) the screens' models: the game screen (composing
 * the controllers its hooks were), the auth screens, the welcome screen, the legal and consent views — and the cookie preferences,
 * one per factory, which the screens open.
 */
export class ViewModelFactory {
  readonly requests: ClientRequests;
  readonly ui: UiState;
  readonly diceAudio: DiceAudio;
  /** The cookie preferences (P3): their provider's state and the openers the screens call — the provider's module state until P3. */
  readonly consent: ConsentModalController;
  /** The 3D scene's WebGL runtime (P4): one per page, as `GameScene.tsx`'s module-level `glRuntime` was. */
  readonly sceneRuntime = new SceneRuntime();
  private router: AuthRouterViewModel | null = null;

  constructor(private readonly deps: IConfigDepViewModels) {
    this.requests = deps.requests;
    this.ui = deps.ui;
    this.diceAudio = new DiceAudio(deps.platform.audioContext, deps.platform.random);
    this.consent = new ConsentModalController(deps.useCases, deps.view, deps.requests);
  }

  /** The table's video chat (P2): one per mount of the game screen. */
  liveKitSession(): LiveKitSessionController {
    const session: IConfigDepLiveKitSession = {
      useCases: this.deps.useCases, view: this.deps.view, requests: this.requests, liveKit: this.deps.liveKit, platform: this.deps.platform,
    };
    return new LiveKitSessionController(session);
  }

  audioRouteMonitor(): AudioRouteMonitor {
    return new AudioRouteMonitor(this.deps.platform.mediaDevices, this.deps.platform.timers);
  }

  disconnectionMonitor(overlays: IPortDisconnectionOverlay): DisconnectionMonitor {
    return new DisconnectionMonitor(overlays);
  }

  pageUnloadGuard(session: IPortPageUnloadGuard): PageUnloadGuard {
    return new PageUnloadGuard(session, this.deps.platform.address, this.deps.platform.pageEvents);
  }

  identityModal(): IdentityModalController {
    return new IdentityModalController();
  }

  legalDocument(): LegalDocumentLoader {
    return new LegalDocumentLoader(this.deps.useCases, this.requests);
  }

  paymentBalancePoller(): PaymentBalancePoller {
    return new PaymentBalancePoller(this.deps.useCases, this.requests, this.deps.platform.timers);
  }

  referralValidation(): ReferralValidation {
    return new ReferralValidation(this.deps.useCases, this.requests);
  }

  /** The game screen (P3): one per mount, composing the controllers its hooks were — the video chat, the name modal, the
   *  disconnection overlays, the unload guard. */
  appRoot(): AppRootViewModel {
    const root: IConfigDepAppRoot = {
      useCases: this.deps.useCases, view: this.deps.view, requests: this.requests, ui: this.ui, platform: this.deps.platform,
      connection: this.deps.connection, session: this.liveKitSession(), identity: this.identityModal(),
      overlays: (port) => this.disconnectionMonitor(port), unload: (port) => this.pageUnloadGuard(port),
    };
    return new AppRootViewModel(root);
  }

  /** The email entry point (P3), composing the referral field's controller and opening the shared cookie preferences. */
  login(): LoginViewModel {
    const login: IConfigDepLogin = { useCases: this.deps.useCases, requests: this.requests, referral: this.referralValidation(), consent: this.consent };
    return new LoginViewModel(login);
  }

  /** The game-table code (P3). */
  enterCode(): EnterCodeViewModel {
    return new EnterCodeViewModel(this.deps.useCases, this.requests);
  }

  /** The 4-digit code (P3). */
  verifyCode(): VerifyCodeViewModel {
    return new VerifyCodeViewModel(this.deps.useCases, this.deps.view, this.requests);
  }

  /** The profile modal (P3). */
  profile(): ProfileViewModel {
    return new ProfileViewModel(this.deps.useCases, this.deps.view, this.requests);
  }

  /** The landing screen (P3), over the stage its view draws; its sound makes a Web Audio context per play. */
  welcome(stage: IServiceWelcomeStage): WelcomeViewModel {
    const { platform } = this.deps;
    const welcome: IConfigDepWelcome = {
      useCases: this.deps.useCases, view: this.deps.view, requests: this.requests, platform,
      sound: new DiceRollingSound(platform.audioContext, platform.random, platform.timers), consent: this.consent, stage,
    };
    return new WelcomeViewModel(welcome);
  }

  /** The legal modal's tab (P3), seeded with the tab it first opens on. */
  legalModal(initialTab: LegalTab): LegalModalViewModel {
    return new LegalModalViewModel(initialTab);
  }

  /** The re-acceptance gate (P3). */
  consentReacceptance(): ConsentReacceptanceViewModel {
    return new ConsentReacceptanceViewModel(this.deps.useCases, this.requests);
  }

  /** The account deletion (P3). */
  gdprDeletion(): GdprDeletionViewModel {
    return new GdprDeletionViewModel(this.deps.useCases, this.requests);
  }

  /** The data export (P3), downloaded through the platform. */
  gdprExport(): GdprExportViewModel {
    return new GdprExportViewModel(this.deps.useCases, this.requests, this.deps.platform.downloads);
  }

  /** The game scene the 3D canvas and the 2D cup share (P4), with the WebGL probe's answers its view hands it. */
  gameScene(glDebug: IConfigGlDebug): GameSceneViewModel {
    const scene: IConfigDepGameScene = {
      useCases: this.deps.useCases, view: this.deps.view, requests: this.requests, diceAudio: this.diceAudio, platform: this.deps.platform, glDebug,
    };
    return new GameSceneViewModel(scene);
  }

  /** The 3D scene's canvas (P4): whether WebGL holds, and its recovery. */
  sceneCanvas(webglAvailable: boolean): SceneCanvasViewModel {
    const canvas: IConfigDepSceneCanvas = { runtime: this.sceneRuntime, platform: this.deps.platform, webglAvailable };
    return new SceneCanvasViewModel(canvas);
  }

  /** The 3D cup's motion (P4), one per mounted cup scene — with the colour maker its view hands it (three.js stays in the 3D chunk). */
  cupMotion<TColor>(colour: (hex: string) => TColor): CupMotion<TColor> {
    return new CupMotion<TColor>(this.deps.platform.cursor, colour);
  }

  /** The 3D scene's demand-loop warm-up (P4). */
  frameWarmup(): FrameWarmup {
    return new FrameWarmup(this.deps.platform);
  }

  /** The control panel (P4). */
  controlPanel(): ControlPanelViewModel {
    return new ControlPanelViewModel(this.deps.useCases, this.deps.view, this.requests);
  }

  /** The admin's LiveKit line (P4). */
  mediaHealth(): MediaHealthPoller {
    return new MediaHealthPoller(this.deps.useCases, this.requests, this.deps.platform.timers);
  }

  /** The lobby (P4). */
  lobby(): LobbyViewModel {
    const lobby: IConfigDepLobby = { useCases: this.deps.useCases, view: this.deps.view, requests: this.requests, platform: this.deps.platform };
    return new LobbyViewModel(lobby);
  }

  /** The guest's nickname form (P4), with the focus its view hands it. */
  identityForm(focus: () => void): IdentityModalViewModel {
    return new IdentityModalViewModel(this.deps.useCases, this.requests, this.deps.platform.timers, focus);
  }

  /** The reconnecting overlay's clock (P4). */
  reconnectingOverlay(): ReconnectingOverlayViewModel {
    return new ReconnectingOverlayViewModel(this.deps.platform.timers);
  }

  /** The QR modal's Escape (P4). */
  qrModal(): QrModalViewModel {
    return new QrModalViewModel(this.deps.platform.pageEvents);
  }

  /** The ad area's slot (P5). */
  adContainer(): AdContainerViewModel {
    return new AdContainerViewModel(this.deps.platform);
  }

  /** An ad slot's fill (P5): once per mount, or at every refresh its view hands it. */
  adPush(once: boolean): AdPushModel {
    return new AdPushModel(this.deps.platform.ads, once);
  }

  /** The health dots (P5). */
  healthIndicator(): HealthIndicatorViewModel {
    return new HealthIndicatorViewModel(this.deps.useCases, this.deps.view, this.requests, this.deps.platform.timers);
  }

  /** The maintenance banner (P5), over the probe its view hands it. */
  maintenance(): MaintenanceViewModel {
    return new MaintenanceViewModel(this.deps.platform);
  }

  /** The mobile frame (P5), over the element its view draws. */
  mobileLayout(frame: { readonly current: Element | null }, zoneCount: number): MobileLayoutViewModel {
    return new MobileLayoutViewModel(this.deps.platform, frame, zoneCount);
  }

  /** The Monetag banner's rotation (P5), over its view's calls to action. */
  topBannerAd(count: number): TopBannerAdViewModel {
    return new TopBannerAdViewModel(this.deps.platform.timers, count);
  }

  /** A roster tile's width (P5), over the element its view draws. */
  tileWidth(tile: { readonly current: Element | null }): TileWidthModel {
    return new TileWidthModel(this.deps.platform.resizeObserver, tile);
  }

  /** A video tile's volume (P5). */
  tileVolume(): TileVolumeModel {
    return new TileVolumeModel();
  }

  /** A video element's media (P5), over the element its view draws. */
  videoTrack(video: { readonly current: HTMLVideoElement | null }): VideoTrackModel {
    return new VideoTrackModel(video);
  }

  /** The credits button (P5). */
  buyCredits(): BuyCreditsViewModel {
    return new BuyCreditsViewModel(this.deps.useCases, this.deps.view, this.requests, this.deps.platform.address);
  }

  /** The admin's usage report (P5). */
  usageReport(): UsageReportViewModel {
    return new UsageReportViewModel(this.deps.useCases, this.requests, this.deps.platform.clock);
  }

  /** The payment's success screen (P5), composing the balance poll's controller. */
  paymentSuccess(): PaymentSuccessViewModel {
    return new PaymentSuccessViewModel(this.deps.useCases, this.deps.view, this.requests, this.paymentBalancePoller());
  }

  /** The entry funnel's plain steps (P5): the guest choice and the auto-host panel. */
  funnelSteps(): FunnelStepsViewModel {
    return new FunnelStepsViewModel(this.deps.useCases, this.deps.view, this.requests);
  }

  /** A style sheet a view needs on the page (P4). */
  styleSheet(id: string, css: string): PageStyleSheet {
    return new PageStyleSheet(this.deps.platform.styles, id, css);
  }

  /** The first-party cookie modal's choice (P3), seeded with the saved choice's advertising. */
  cookiePreferences(advertising: boolean): CookiePreferencesViewModel {
    return new CookiePreferencesViewModel(advertising);
  }

  /** The page's auth router: one per factory, as the session start is one per page load. */
  authRouter(): AuthRouterViewModel {
    if (this.router === null) {
      const router: IConfigDepAuthRouter = { view: this.deps.view, useCases: this.deps.useCases, requests: this.requests, address: this.deps.platform.address };
      this.router = new AuthRouterViewModel(router);
    }
    return this.router;
  }
}
